import { Save } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { fetchSlaSettings, saveSlaSettings, toSlaApiError, type SlaApiError } from "api";
import { getTranslation } from "common";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useSnackbar, useTranslations } from "hooks";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type { SlaSettingsDto, SlaSettingsInput } from "model/slaModel";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  type ReactNode,
} from "react";

/**
 * **Impostazioni SLA di organizzazione** (piano §5 F4c: "disattivazione org").
 *
 * Rotte **realmente esistenti** lato Server, admin-only:
 * `GET|PUT /sla/organizations/{org}/settings` (`Controller.SLA.pas:89-93`,
 * `MVCDoc('Impostazioni SLA di organizzazione (admin-only)')`, modello
 * `SlaSettings.pas:47` colonna `enabled`). L'adattatore client e'
 * `slaApi.ts:375-401`.
 *
 * Perche' lo stato reso e' **quello del Server**: dopo ogni scrittura il pannello
 * *rilegge* (`fetchSlaSettings`) invece di applicare uno stato ottimistico. La
 * regola di F4b vale anche qui — nessun cambio a insaputa dell'utente: l'attributo
 * `data-settings-source` dichiara da dove viene il valore mostrato e
 * `data-sla-enabled` e' sempre la lettura del Server, mai l'intenzione del click.
 *
 * Non si offre nulla a chi non e' amministratore: la rotta e' admin-only, quindi il
 * pannello **dichiara** il vincolo (`sla-statistics-settings-forbidden`, chiave
 * `sla.monitor.forbidden` gia' usata per i 403) invece di mostrare un interruttore
 * che il Server rifiuterebbe.
 *
 * Impostazioni di organizzazione (`showOrganizationFields`, vista impostazioni
 * dell'organizzazione): con lo SLA attivo, sotto l'interruttore, il pannello rende
 * le altre colonne di `sla_settings` che il PUT legge (`Controller.SLA.pas`, ramo
 * httpPUT: `risk_remaining_percent`, `nudge_remaining_percent`,
 * `escalation_enabled`, `digest_enabled`, `min_duration_minutes`,
 * `max_notification_latency_seconds`). `digest_at` non e' letto dal PUT: e'
 * mostrato in sola lettura. La rotta non ha `expected_revision` (l'ultima
 * scrittura vince): dopo il salvataggio il pannello rilegge.
 *
 * **Un solo "Salva"** per tutta la vista: l'interruttore `enabled` non scrive piu'
 * al click ma entra nella bozza come gli altri campi; il pulsante unico
 * (`btn-save-sla-organization-settings`) manda **un** PUT, attivo solo se la bozza
 * differisce dalla lettura del Server (dirty-check) ed e' valida. Il payload e'
 * quello di prima: `enabled` compare solo se cambiato; con lo SLA spento si manda
 * solo `{ enabled: false }` (i campi sotto non sono resi); con lo SLA acceso si
 * mandano i sei campi letti dal PUT, `digest_enabled` incluso col valore attuale.
 *
 * Errori del salvataggio: tenuti in uno stato proprio (`saveError`), perche' la
 * rilettura che segue ogni scrittura azzera l'errore di lettura. Un 400 con una
 * chiave `sla_*` (`detailedmessage`) si traduce dal catalogo; altrimenti
 * `sla.settings.save_error`.
 *
 * Vista impostazioni dell'organizzazione (`hideSaveButton`): il pulsante del
 * pannello non si rende e il salvataggio passa al "Salva" principale della
 * pagina (`btn-save-organization`), che legge lo stato con `onSaveStateChange`
 * e chiama `save()` dal ref. Payload, validazione e dirty-check restano questi.
 *
 * `children` riceve le impostazioni lette e si rende solo con lo SLA attivo: la
 * vista impostazioni ci monta la configurazione per modello, senza navigare. Con
 * lo SLA disattivo il pannello mostra al suo posto un breve suggerimento.
 */
interface SlaOrganizationSettingsPanelProps {
  idorganization: number;
  /** Ruolo dell'utente nell'organizzazione scelta (rotta admin-only). */
  isAdmin: boolean;
  /** Rende il modulo delle impostazioni di organizzazione sotto l'interruttore. */
  showOrganizationFields?: boolean;
  /** Configurazione resa sotto, solo con lo SLA attivo (lettura del Server). */
  children?: (settings: SlaSettingsDto) => ReactNode;
  /** Non rende il "Salva" del pannello: lo guida la pagina via ref. */
  hideSaveButton?: boolean;
  /** Stato del salvataggio per il pulsante della pagina. */
  onSaveStateChange?: (state: SlaSettingsSaveState) => void;
}

/** Stato esposto alla pagina che ospita il pannello. */
export interface SlaSettingsSaveState {
  /** Bozza diversa dalla lettura del Server. */
  dirty: boolean;
  /** Bozza diversa e valida: il PUT si puo' mandare. */
  canSave: boolean;
  /** Lettura o scrittura in corso. */
  busy: boolean;
}

/** Handle per il "Salva" della pagina. */
export interface SlaOrganizationSettingsHandle {
  /** Manda il PUT se la bozza e' salvabile; `false` se il Server rifiuta. */
  save: () => Promise<boolean>;
}

/** Bozza del modulo: stringhe, cosi' un campo vuoto resta modificabile. */
interface SettingsDraft {
  enabled: boolean;
  riskremainingpercent: string;
  nudgeremainingpercent: string;
  escalationenabled: boolean;
  digestenabled: boolean;
  mindurationminutes: string;
  maxnotificationlatencyseconds: string;
}

type NumericDraftField =
  | "riskremainingpercent"
  | "nudgeremainingpercent"
  | "mindurationminutes"
  | "maxnotificationlatencyseconds";

const toDraft = (settings: SlaSettingsDto): SettingsDraft => ({
  enabled: Boolean(settings.enabled),
  riskremainingpercent: String(settings.riskremainingpercent ?? ""),
  nudgeremainingpercent: String(settings.nudgeremainingpercent ?? ""),
  escalationenabled: Boolean(settings.escalationenabled),
  digestenabled: Boolean(settings.digestenabled),
  mindurationminutes: String(settings.mindurationminutes ?? ""),
  maxnotificationlatencyseconds: String(settings.maxnotificationlatencyseconds ?? ""),
});

/** Intero nell'intervallo chiuso, altrimenti `null`. */
const parseIntInRange = (value: string, min: number, max: number): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return parsed >= min && parsed <= max ? parsed : null;
};

const TEST_ID = "sla-statistics-settings";

/**
 * Hidden for now: "riepilogo giornaliero" di organizzazione (interruttore
 * `digest_enabled` e orario `digest_at` in sola lettura), come il blocco
 * "Notifiche SLA". Solo UI: la bozza tiene `digestenabled` letto dal Server e il
 * PUT lo manda invariato. Per riattivarli basta `true`.
 */
const SHOW_DIGEST_FIELDS = false;

const SlaOrganizationSettingsPanel = forwardRef<
  SlaOrganizationSettingsHandle,
  SlaOrganizationSettingsPanelProps
>(function SlaOrganizationSettingsPanel(
  {
    idorganization,
    isAdmin,
    showOrganizationFields = false,
    children,
    hideSaveButton = false,
    onSaveStateChange,
  },
  ref,
) {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [settings, setSettings] = useState<SlaSettingsDto | null>(null);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<SlaApiError | null>(null);
  /** Errore dell'ultimo salvataggio: sopravvive alla rilettura che lo segue. */
  const [saveError, setSaveError] = useState<SlaApiError | null>(null);

  const t = useCallback(
    (key: string): string => getTranslation(translations, key),
    [translations],
  );

  /** Lettura: e' la sola fonte dello stato reso (`data-settings-source`). */
  const load = useCallback(async () => {
    if (!isAdmin) {
      setSettings(null);
      setDraft(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchSlaSettings(idorganization);
      setSettings(response);
      setDraft(response ? toDraft(response) : null);
      setError(null);
    } catch (caught) {
      setSettings(null);
      setDraft(null);
      setError(toSlaApiError(caught));
    } finally {
      setLoading(false);
    }
  }, [idorganization, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  /** Scrittura esplicita, poi rilettura: mai uno stato ottimistico. */
  const write = async (body: SlaSettingsInput): Promise<boolean> => {
    setLoading(true);
    setSaveError(null);
    try {
      const response = await saveSlaSettings(idorganization, body);
      setError(null);
      if (response?.responsemessage) {
        sendSnack({ message: response.responsemessage, type: "success" });
      }
      return true;
    } catch (caught) {
      setSaveError(toSlaApiError(caught));
      return false;
    } finally {
      await load();
    }
  };

  const risk = draft ? parseIntInRange(draft.riskremainingpercent, 1, 99) : null;
  const nudge = draft ? parseIntInRange(draft.nudgeremainingpercent, 1, 99) : null;
  const minDuration = draft ? parseIntInRange(draft.mindurationminutes, 0, 100000) : null;
  const maxLatency = draft
    ? parseIntInRange(draft.maxnotificationlatencyseconds, 1, 86400)
    : null;
  const draftValid =
    risk !== null && nudge !== null && minDuration !== null && maxLatency !== null;

  /** Bozza diversa dalla lettura del Server: abilita il "Salva" unico. */
  const dirty =
    Boolean(settings && draft) &&
    JSON.stringify(draft) !== JSON.stringify(settings ? toDraft(settings) : null);
  /** Con lo SLA spento i campi sotto non sono resi e non si validano. */
  const canSave = dirty && (!draft?.enabled || draftValid);

  /**
   * "Salva" unico: un solo PUT con tutto cio' che la vista rende. `enabled` solo
   * se cambiato; SLA spento -> `{ enabled: false }`; SLA acceso -> i sei campi.
   */
  const saveAll = async (): Promise<boolean> => {
    if (!settings || !draft || !canSave) return true;
    const enabledChanged = draft.enabled !== Boolean(settings.enabled);
    if (!draft.enabled) {
      return write({ enabled: false });
    }
    if (risk === null || nudge === null || minDuration === null || maxLatency === null) {
      return false;
    }
    return write({
      ...(enabledChanged ? { enabled: true } : {}),
      risk_remaining_percent: risk,
      nudge_remaining_percent: nudge,
      escalation_enabled: draft.escalationenabled,
      digest_enabled: draft.digestenabled,
      min_duration_minutes: minDuration,
      max_notification_latency_seconds: maxLatency,
    });
  };

  useImperativeHandle(ref, () => ({ save: saveAll }));

  useEffect(() => {
    onSaveStateChange?.({ dirty, canSave, busy: loading });
  }, [onSaveStateChange, dirty, canSave, loading]);

  const setDraftField = (patch: Partial<SettingsDraft>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const numberField = (
    field: NumericDraftField,
    labelKey: string,
    invalid: boolean,
    min: number,
    max: number,
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        id={`${TEST_ID}-${field}`}
        type="number"
        size="small"
        fullWidth
        label={t(labelKey)}
        value={draft?.[field] ?? ""}
        error={invalid}
        disabled={loading}
        inputProps={{ min, max, step: 1 }}
        onChange={({ target }) => setDraftField({ [field]: target.value })}
      />
    </Grid>
  );

  const showBelow = showOrganizationFields || Boolean(children);

  return (
    <Stack
      spacing={1}
      data-testid={`${TEST_ID}-panel`}
      data-sla-admin={isAdmin ? "true" : "false"}
      data-sla-enabled={settings ? String(settings.enabled) : ""}
      data-settings-source="sla/organizations/settings"
    >
      <Typography variant="subtitle1" data-testid={`${TEST_ID}-title`}>
        {t("sla.settings.title")}
      </Typography>

      {!isAdmin && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`${TEST_ID}-forbidden`}
          data-error-key={SLA_MONITOR_TEXT_KEYS.forbidden}
        >
          {t(SLA_MONITOR_TEXT_KEYS.forbidden)}
        </Typography>
      )}

      {isAdmin && loading && (
        <CircularProgress size={20} data-testid={`${TEST_ID}-loading`} />
      )}

      {isAdmin && error && (
        <Alert
          severity="error"
          data-testid={`${TEST_ID}-error`}
          data-error-status={error.status === null ? "" : String(error.status)}
        >
          {t(
            error.status === 403
              ? SLA_MONITOR_TEXT_KEYS.forbidden
              : SLA_MONITOR_TEXT_KEYS.loadError,
          )}
        </Alert>
      )}

      {isAdmin && saveError && (
        <Alert
          severity="error"
          data-testid={`${TEST_ID}-save-error`}
          data-error-status={saveError.status === null ? "" : String(saveError.status)}
          data-error-key={saveError.messageKey ?? ""}
        >
          {t(
            saveError.messageKey ??
              (saveError.status === 403
                ? SLA_MONITOR_TEXT_KEYS.forbidden
                : "sla.settings.save_error"),
          )}
        </Alert>
      )}

      {isAdmin && settings && draft && (
        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled}
              disabled={loading}
              onChange={({ target }) => setDraftField({ enabled: target.checked })}
              inputProps={{
                "aria-label": t("sla.settings.enabled"),
                // `inputProps` tipizza gli attributi HTML standard: il testid
                // dell'input reale (quello che porta lo stato) va passato senza
                // perdere il tipo, come in `SlaActivitiesSurface.tsx:236-241`.
                ...({ "data-testid": `${TEST_ID}-enabled` } as Record<string, string>),
              }}
            />
          }
          label={
            <Typography variant="body2" data-testid={`${TEST_ID}-enabled-label`}>
              {t("sla.settings.enabled")}
            </Typography>
          }
        />
      )}

      {isAdmin && settings && draft && !draft.enabled && showBelow && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={`${TEST_ID}-disabled-hint`}
        >
          {t("sla.settings.disabled_hint")}
        </Typography>
      )}

      {isAdmin && settings && showOrganizationFields && draft?.enabled && (
        <Box data-testid={`${TEST_ID}-organization-fields`} sx={{ pt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t("sla.settings.organization_defaults")}
          </Typography>
          <Grid container spacing={2}>
            {numberField("riskremainingpercent", "sla.settings.risk_percent", risk === null, 1, 99)}
            {numberField("nudgeremainingpercent", "sla.settings.nudge_percent", nudge === null, 1, 99)}
            {numberField("mindurationminutes", "sla.settings.min_duration", minDuration === null, 0, 100000)}
            {numberField(
              "maxnotificationlatencyseconds",
              "sla.settings.max_latency_seconds",
              maxLatency === null,
              1,
              86400,
            )}
            {SHOW_DIGEST_FIELDS && (
            <Grid item xs={12} sm={6} md={4}>
              {/* `digest_at` non e' letto dal PUT: sola lettura. */}
              <TextField
                id={`${TEST_ID}-digestat`}
                size="small"
                fullWidth
                disabled
                label={t("sla.preferences.digest.at")}
                value={(settings.digestat ?? "").slice(0, 5)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            )}
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.escalationenabled}
                      disabled={loading}
                      onChange={({ target }) => setDraftField({ escalationenabled: target.checked })}
                      inputProps={
                        { "data-testid": `${TEST_ID}-escalationenabled` } as Record<string, string>
                      }
                    />
                  }
                  label={<Typography variant="body2">{t("sla.settings.escalation_enabled")}</Typography>}
                />
                {SHOW_DIGEST_FIELDS && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.digestenabled}
                      disabled={loading}
                      onChange={({ target }) => setDraftField({ digestenabled: target.checked })}
                      inputProps={
                        { "data-testid": `${TEST_ID}-digestenabled` } as Record<string, string>
                      }
                    />
                  }
                  label={<Typography variant="body2">{t("sla.preferences.digest.enabled")}</Typography>}
                />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* "Salva" unico della vista (interruttore + impostazioni di
          organizzazione): un solo PUT, una sola attesa, un solo esito. */}
      {isAdmin && settings && draft && !hideSaveButton && (
        <Box sx={{ textAlign: "right" }}>
          <FlowentiButton
            id="btn-save-sla-organization-settings"
            data-testid={`${TEST_ID}-save`}
            data-dirty={dirty ? "true" : "false"}
            variant="contained"
            size="small"
            startIcon={<Save />}
            disabled={loading || !canSave}
            onClick={saveAll}
          >
            {t("pm.button.save")}
          </FlowentiButton>
        </Box>
      )}

      {isAdmin && settings?.enabled && children && (
        <Box data-testid={`${TEST_ID}-configuration`} sx={{ pt: 2 }}>
          {children(settings)}
        </Box>
      )}
    </Stack>
  );
});

export default SlaOrganizationSettingsPanel;
